/*
This file is a helper file that is used to create a policy for the Trusted Types API and a fallback for browsers that do not support it.
The Trusted Types API is a security feature that helps prevent cross-site scripting (XSS) attacks.
 */
export interface TrustedTypeOptions {
  createHTML?: (...args: any[]) => string | undefined;
  createScript?: (...args: any[]) => string | undefined;
  createScriptURL?: (...args: any[]) => string | undefined;
}

/**
 * Unique key used to store the policy registry on globalThis.
 * Symbol.for ensures the same symbol is shared across all module instances and bundles,
 * preventing duplicate policy creation when the package is consumed in multiple contexts.
 */
const GLOBAL_POLICY_REGISTRY_KEY = Symbol.for(
  "ooui-trusted-types-policy-registry"
);

/**
 * Returns the global registry of Trusted Types policies.
 * Uses globalThis to ensure a single shared registry across all module instances,
 * even when the package is bundled multiple times in different contexts.
 * Trusted Types throws an error if a policy with the same name is created more than once,
 * so this global registry prevents duplicate policy creation.
 *
 * The registry is attached to globalThis using Object.defineProperty with
 * non-enumerable and non-configurable flags to reduce the risk of accidental
 * discovery or external tampering.
 * @returns The global map of registered policies keyed by policy name.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getGlobalPolicyRegistry(): Record<string, any> {
  const global = (
    typeof globalThis !== "undefined" ? globalThis : self
  ) as Record<symbol, Record<string, any> | undefined>; // eslint-disable-line @typescript-eslint/no-explicit-any

  if (!global[GLOBAL_POLICY_REGISTRY_KEY]) {
    Object.defineProperty(global, GLOBAL_POLICY_REGISTRY_KEY, {
      value: Object.create(null),
      enumerable: false,
      configurable: false,
      writable: false,
    });
  }

  return global[GLOBAL_POLICY_REGISTRY_KEY]!;
}

/**
 * This is a fallback for browsers that do not support the Trusted Types API.
 * It is a tinyfill for the API.
 */
const trustedTypesTinyFill = {
  createPolicy: (_: string, options: any) => options,
};
/**
 * Default Trusted Types API coming from the browser.
 */
const defaultTrustedTypesApi =
  typeof self !== "undefined" && self.trustedTypes
    ? self.trustedTypes
    : trustedTypesTinyFill;

/**
 * This function creates a policy for the Trusted Types API.
 * If the policy has already been created, it will skip the creation.
 * @param policyName - The name of the policy.
 * @param policyOptions - The options for the policy.
 * @param trustedTypesApi - The Trusted Types API, this strictly used for testing. Default should be used always in production.
 * @returns The policy.
 */
export const createPolicy = (
  policyName: string,
  policyOptions: TrustedTypeOptions,
  trustedTypesApi = defaultTrustedTypesApi,
  errorLogFunction?: (message: string) => void,
  shouldThrowOnCreationFailure: boolean = false
) => {
  const registry = getGlobalPolicyRegistry();

  try {
    if (!registry[policyName]) {
      registry[policyName] = trustedTypesApi.createPolicy(policyName, {
        createHTML: policyOptions.createHTML,
        createScript: policyOptions.createScript,
        createScriptURL: policyOptions.createScriptURL,
      });
    }
  } catch (e) {
    errorLogFunction?.(`${e}`);
    if (shouldThrowOnCreationFailure) {
      throw e;
    }
  }

  return registry[policyName];
};
